import React, { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { actionErrorText } from '../../../../shared/toastHelper';

import DealTypeSelector from './dealForm/DealTypeSelector';
import DepositRows from './dealForm/DepositRows';
import BonusRows from './dealForm/BonusRows';
import { buildDealFields, dealTypeFromOffer, buildOfferPatch, buildDepositParams, buildBonusParams, DEAL_TYPES } from './dealForm/dealParams';
import SimpleTaxWithholdingSection from './SimpleTaxWithholdingSection';
import SimpleMerchandisingSection from './SimpleMerchandisingSection';
import ModifierSwitch from './ModifierSwitch';
import ArtistPickerField from '../../../shared/components/ArtistPickerField';
import SimpleTemplateCard from './SimpleTemplateCard';
import { forOffer } from '../../utils/offerScopedRows';
import SimplePoolShareSection, { POOL_MEMBER_DEAL_FIELDS } from './SimplePoolShareSection';

const DESCRIPTION_MAX = 250;

const numOrEmpty = (v) => (v === null || v === undefined ? '' : String(v));

const depositToRow = (d) => ({
  id: d.id,
  depositType: d.deposit_type || 'percent',
  value: numOrEmpty(d.value),
  dueDateType: d.due_date_type || 'relative',
  days: numOrEmpty(d.relative_due_date),
  specificDate: d.specific_due_date || '',
});

const bonusToRow = (b) => {
  const isPercentage = b.dollar_or_percentage_operator === 'PERCENTAGE';
  return {
    id: b.id,
    operator: isPercentage ? 'PERCENTAGE' : 'CASH',
    ticketThreshold: numOrEmpty(b.ticket_count_requirement),
    amount: numOrEmpty(isPercentage ? b.upside_percentage : b.dollar_amount),
  };
};

// `control` renders to the left of the title (e.g. the modifier on/off switch).
// It sits beside the collapse button rather than inside it so its own button
// isn't nested in the header button.
const CollapsibleRow = ({ title, expanded, onToggle, control, children }) => (
  <div className="simple-deal-form__collapsible" data-testid="collapsible-row">
    <div className="simple-deal-form__collapsible-header">
      {control}
      <button
        type="button"
        className="simple-deal-form__collapsible-toggle"
        aria-expanded={expanded}
        onClick={onToggle}
      >
        <span className="simple-deal-form__collapsible-title">{title}</span>
        <span className={`simple-deal-form__chevron ${expanded ? 'simple-deal-form__chevron--open' : ''}`} aria-hidden="true">
          ›
        </span>
      </button>
    </div>
    {expanded && <div className="simple-deal-form__collapsible-body">{children}</div>}
  </div>
);

CollapsibleRow.propTypes = {
  title: PropTypes.string.isRequired,
  expanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  control: PropTypes.node,
  children: PropTypes.node.isRequired,
};

CollapsibleRow.defaultProps = { control: null };

// Rendered top and bottom so the save action stays above the fold (O-4320).
const DealFormActions = ({ saving, dirty, onCancel, onSave }) => (
  <div className="simple-deal-form__actions">
    <button type="button" className="simple-offer__cancel" onClick={onCancel}>Cancel</button>
    <button
      type="button"
      className={`simple-offer__save${!saving && !dirty ? ' simple-offer__save--clean' : ''}`}
      disabled={saving}
      onClick={onSave}
    >
      {saving ? 'Saving…' : 'Save deal'}
    </button>
  </div>
);

DealFormActions.propTypes = {
  saving: PropTypes.bool.isRequired,
  dirty: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

const PromoterProfitSection = ({ offer, onChangeField }) => {
  const on = !!offer.promoter_profit;

  return (
    <div className="simple-mod" data-testid="modifiers-subsection-promoter-profit">
      {on && (
        <div className="simple-mod__grid">
          <label className="simple-mod__field simple-mod__field--narrow">
            <span className="simple-mod__label">Promoter profit</span>
            <span className="simple-mod__input-wrap">
              <input
                type="number"
                step="0.01"
                className="simple-mod__input simple-mod__input--suffix"
                placeholder="0.0"
                value={offer.promoter_profit_percentage ?? ''}
                onChange={(e) => onChangeField(
                  'promoter_profit_percentage',
                  e.target.value === '' ? null : Number(e.target.value),
                )}
              />
              <span className="simple-mod__affix simple-mod__affix--suffix"><i className="fas fa-percent" aria-hidden="true" /></span>
            </span>
          </label>
        </div>
      )}
    </div>
  );
};

PromoterProfitSection.propTypes = {
  offer: PropTypes.object.isRequired,
  onChangeField: PropTypes.func.isRequired,
};

const SimpleCreateArtistDealForm = ({ ctrl, onClose, mode, defaultRole }) => {
  const { offer } = ctrl;
  const performer = offer.primary_performer;
  const artistName = offer.performer_name || performer?.artistable?.name || 'No artist';

  const isCreate = mode === 'create';

  const [form, setForm] = useState(() => {
    if (isCreate) {
      return {
        role: defaultRole,
        dealType: DEAL_TYPES.FLAT,
        guarantee: '',
        percentage: '',
        breakeven: '',
        revenueBase: 'default',
        dealTermsLanguage: '',
        description: '',
      };
    }
    return {
      role: performer?.act_type === 'support' ? 'support' : 'headline',
      dealType: dealTypeFromOffer({
        dollar_amount: offer.dollar_amount,
        upside_percentage: offer.upside_percentage,
        dollar_or_percentage_operator: offer.dollar_or_percentage_operator,
      }),
      guarantee: numOrEmpty(offer.dollar_amount),
      percentage: numOrEmpty(offer.upside_percentage),
      breakeven: numOrEmpty(offer.breakeven_threshold),
      revenueBase: offer.revenue_base || 'default',
      dealTermsLanguage: offer.deal_terms_language || '',
      description: offer.guarantee_notes || '',
    };
  });

  const initialDeposits = useMemo(
    () => (isCreate ? [] : forOffer(ctrl.budget?.v3_budget_deposits, offer.id).map(depositToRow)),
    [ctrl.budget, isCreate, offer.id],
  );
  const [deposits, setDeposits] = useState(initialDeposits);
  const serverDepositIds = useRef(initialDeposits.filter((d) => d.id).map((d) => d.id));

  const initialBonuses = useMemo(
    () => (isCreate ? [] : forOffer(ctrl.budget?.v3_budget_bonuses, offer.id).map(bonusToRow)),
    [ctrl.budget, isCreate, offer.id],
  );
  const [bonuses, setBonuses] = useState(initialBonuses);
  const serverBonusIds = useRef(initialBonuses.filter((b) => b.id).map((b) => b.id));

  const [expanded, setExpanded] = useState({ tax: false, taxAfterFff: false, merch: false, promoter: false });
  const toggle = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [dirty, setDirty] = useState(false);

  const [picked, setPicked] = useState(null);

  const patch = (changes) => {
    setDirty(true);
    setForm((prev) => ({ ...prev, ...changes }));
  };

  const changeDeposits = (next) => { setDirty(true); setDeposits(next); };
  const changeBonuses = (next) => { setDirty(true); setBonuses(next); };
  const pickArtist = (p) => { setDirty(true); setPicked(p); };

  const onChangeModifierField = (field, value) => {
    setDirty(true);
    ctrl.handleChangeModifierField(field, value);
  };

  // Flip a modifier's enabled flag from its header switch; turning it on also
  // expands the tray so the just-enabled fields are visible.
  const toggleModifier = (field, expandKey) => {
    const next = !offer[field];
    onChangeModifierField(field, next);
    if (next) setExpanded((prev) => ({ ...prev, [expandKey]: true }));
  };

  // Use the same header casing as the global axios default
  // (axios.defaults.headers.common['X-CSRF-TOKEN']) — axios 0.19 merges headers
  // case-sensitively, so a mixed-case 'X-CSRF-Token' here would send a SECOND
  // header alongside the default and the server would reject the doubled token.
  const headers = { 'X-CSRF-TOKEN': ctrl.csrfToken };
  const depositUrl = (id) => `/teams/${ctrl.team.id}/budget_deposits/v3${id ? `/${id}` : ''}`;
  const bonusUrl = (id) => `/teams/${ctrl.team.id}/budget_bonuses/v3${id ? `/${id}` : ''}`;
  const v3BudgetId = offer.v3_budget_id || ctrl.budget?.id;

  const syncDeposits = async (working, budgetId = v3BudgetId, offerId = offer.id) => {
    const currentIds = working.filter((d) => d.id).map((d) => d.id);
    const removed = serverDepositIds.current.filter((id) => !currentIds.includes(id));
    for (const id of removed) {
      // eslint-disable-next-line no-await-in-loop
      await axios.delete(depositUrl(id), { headers });
      serverDepositIds.current = serverDepositIds.current.filter((x) => x !== id);
    }

    for (let i = 0; i < working.length; i += 1) {
      const d = working[i];
      if (d.value === '' || d.value == null) continue;
      const body = buildDepositParams({ v3BudgetId: budgetId, v3OfferId: offerId, ...d });
      if (d.id) {
        // eslint-disable-next-line no-await-in-loop
        await axios.patch(depositUrl(d.id), body, { headers });
      } else {
        // eslint-disable-next-line no-await-in-loop
        const { data } = await axios.post(depositUrl(), body, { headers });
        working[i] = { ...d, id: data.id };
        serverDepositIds.current.push(data.id);
      }
    }
    return working;
  };

  const syncBonuses = async (working, budgetId = v3BudgetId, offerId = offer.id) => {
    const currentIds = working.filter((b) => b.id).map((b) => b.id);
    const removed = serverBonusIds.current.filter((id) => !currentIds.includes(id));
    for (const id of removed) {
      // eslint-disable-next-line no-await-in-loop
      await axios.delete(bonusUrl(id), { headers });
      serverBonusIds.current = serverBonusIds.current.filter((x) => x !== id);
    }

    for (let i = 0; i < working.length; i += 1) {
      const b = working[i];
      if (b.amount === '' || b.amount == null) continue;
      const body = buildBonusParams({ v3BudgetId: budgetId, v3OfferId: offerId, ...b });
      if (b.id) {
        // eslint-disable-next-line no-await-in-loop
        await axios.patch(bonusUrl(b.id), body, { headers });
      } else {
        // eslint-disable-next-line no-await-in-loop
        const { data } = await axios.post(bonusUrl(), body, { headers });
        working[i] = { ...b, id: data.id };
        serverBonusIds.current.push(data.id);
      }
    }
    return working;
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    setError('');
    // Land any buffered pool-card edit BEFORE this save, so the two are not in
    // flight together — the deal response carries the pre-edit pool columns and
    // would revert them through SET_OFFER if it answered second.
    await ctrl.flushModifierFields?.();
    const working = [...deposits];
    const workingBonuses = [...bonuses];
    try {
      // The deal-type card is hidden in pool mode, so its `form` values are the
      // mount-time snapshot and would revert whatever the pool share card has
      // since written. Those columns belong to the pool card there.
      const patch = buildOfferPatch({ form, performerId: performer?.id }).v3_offer;
      if (ctrl.poolActive) {
        POOL_MEMBER_DEAL_FIELDS.forEach((f) => { delete patch[f]; });
      }
      const updated = await ctrl.updateOffer(offer.id, patch);
      if (!updated) {
        setSaving(false);
        setError('Something went wrong saving the offer.');
        return;
      }
      await syncDeposits(working);
      await syncBonuses(workingBonuses);
      setDeposits(working);
      setBonuses(workingBonuses);
      // Pull the just-saved deposits/bonuses back into the budget context so
      // reopening the form reseeds from fresh data without a page reload.
      await ctrl.refreshBudgetChildren?.();
      setSaving(false);
      onClose();
    } catch (e) {
      setDeposits(working);
      setBonuses(workingBonuses);
      setError('Something went wrong saving the deal.');
      setSaving(false);
    }
  };

  const handleSaveCreate = async () => {
    if (!picked) {
      setError('Pick an artist');
      return;
    }
    setSaving(true);
    setError('');

    const performerAttributes = { act_type: form.role, artistable_type: 'Artist', artistable_id: picked.artistId };

    const o = offer;
    const payload = {
      v3_offer: {
        calendar_event_id: o.calendar_event_id || o.calendar_event?.id,
        v3_budget_id: o.v3_budget_id,
        venue_id: o.venue_id || o.venue?.id,
        pipeline_id: o.pipeline_id,
        event_name: o.event_name || '',
        ...(o.v3_offer_pool_id ? { v3_offer_pool_id: o.v3_offer_pool_id } : {}),
        primary_performer_attributes: performerAttributes,
        ...buildDealFields(form),
        guarantee_notes: form.description || '',
        // Created from the simple editor — so it reloads there (O-3412).
        editor_variant: 'v3_simple',
      },
    };

    try {
      const { data: newOffer } = await axios.post(
        `/teams/${ctrl.team.id}/offers/v3`,
        payload,
        { headers },
      );

      const working = [...deposits];
      const workingBonuses = [...bonuses];
      try {
        // Deposits and bonuses must attach to the newly created offer's budget,
        // not the create-context offer's budget that v3BudgetId closes over.
        await syncDeposits(working, newOffer.v3_budget_id, newOffer.id);
        await syncBonuses(workingBonuses, newOffer.v3_budget_id, newOffer.id);
        setDeposits(working);
        setBonuses(workingBonuses);
      } catch (depositErr) {
        setError('Offer created but deposits could not be saved.');
        setSaving(false);
        await ctrl.handleSelectOffer(newOffer.id);
        onClose();
        return;
      }

      await ctrl.handleSelectOffer(newOffer.id);
      onClose();
    } catch (e) {
      setError(actionErrorText(e, 'Something went wrong saving the deal.'));
      setSaving(false);
    }
  };

  const handleSave = isCreate ? handleSaveCreate : handleSaveEdit;

  return (
    <div className="simple-deal-form" data-testid="simple-create-artist-deal-form">
      <h2 className="simple-deal-form__heading">Create Artist Deal</h2>

      {error && (
        <div className="simple-deal-form__error" role="alert">{error}</div>
      )}

      <DealFormActions saving={saving} dirty={dirty} onCancel={onClose} onSave={handleSave} />

      <section className="simple-offer__section">
        <h3 className="simple-offer__section-title">ARTIST</h3>
        <div className="simple-offer__artist-row">
          <div className="simple-offer__field simple-offer__field--artist">
            <span className="simple-offer__label">Artist name</span>
            {isCreate ? (
              picked ? (
                <div className="simple-offer__selected-artist" data-testid="selected-artist">
                  <span className="simple-offer__selected-artist-name">
                    {picked.artistName}
                  </span>
                  <button
                    type="button"
                    className="simple-offer__change-artist"
                    onClick={() => setPicked(null)}
                  >
                    Change
                  </button>
                </div>
              ) : (
                <ArtistPickerField
                  placeholder="Search or add an artist…"
                  description="Search your CRM, or add a new artist inline."
                  teamId={ctrl.team?.id}
                  onSelect={(sel) => pickArtist({ artistId: sel.artistId, artistName: sel.artist?.name })}
                />
              )
            ) : (
              <div className="simple-offer__selected-artist" data-testid="selected-artist">
                <span className="simple-offer__selected-artist-name">{artistName}</span>
                {ctrl.openChangeArtist && (
                  <button
                    type="button"
                    className="simple-offer__change-artist-pencil"
                    onClick={ctrl.openChangeArtist}
                    aria-label="Change artist"
                    title="Change artist"
                    data-testid="deal-form-change-artist"
                  >
                    <i className="far fa-pen" aria-hidden="true" />
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="simple-offer__field simple-offer__field--billing">
            <span className="simple-offer__label">Billing</span>
            <div className="deal-terms-segmented" role="radiogroup" aria-label="Billing">
              <button
                type="button"
                className={`deal-terms-segmented__btn ${form.role === 'headline' ? 'deal-terms-segmented__btn--active' : ''}`}
                aria-pressed={form.role === 'headline'}
                onClick={() => patch({ role: 'headline' })}
              >
                Headliner
              </button>
              <button
                type="button"
                className={`deal-terms-segmented__btn ${form.role === 'support' ? 'deal-terms-segmented__btn--active' : ''}`}
                aria-pressed={form.role === 'support'}
                onClick={() => patch({ role: 'support' })}
              >
                Support
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* O-4756. In pool mode the payout terms live on the POOL deal and the
        * member's own cut lives on the pool share card below — so this card would
        * be a second, contradicting set of controls for the same columns. The
        * advanced editor hides its equivalent for the same reason
        * (V3OfferDealTermsForm: `{!poolMode && ...}`). Leaving it visible also let
        * Save deal write its frozen `form` values straight over a guarantee and
        * combiner the pool card had just persisted. */}
      {!ctrl.poolActive && (
      <DealTypeSelector
        dealType={form.dealType}
        guarantee={form.guarantee}
        percentage={form.percentage}
        breakeven={form.breakeven}
        revenueBase={form.revenueBase}
        dealTermsLanguage={form.dealTermsLanguage}
        onChange={patch}
        leadingCard={isCreate ? null : <SimpleTemplateCard offer={offer} />}
      />
      )}

      <section className="simple-offer__section">
        <label className="simple-offer__field">
          <span className="simple-offer__label">Description</span>
          <textarea
            data-testid="description"
            className="simple-offer__textarea"
            maxLength={DESCRIPTION_MAX}
            value={form.description}
            onChange={(e) => patch({ description: e.target.value })}
          />
          <span className="simple-offer__hint">Shown on the offer sheet sent to the agent. {DESCRIPTION_MAX} characters max.</span>
        </label>
      </section>

      <DepositRows deposits={deposits} onChange={changeDeposits} />

      <BonusRows bonuses={bonuses} onChange={changeBonuses} />

      {!isCreate && (
        <section className="simple-offer__section">
          <CollapsibleRow
            title="Tax Withholding"
            expanded={expanded.tax}
            onToggle={() => toggle('tax')}
            control={(
              <ModifierSwitch
                on={!!offer.has_tax_withholding}
                onToggle={() => toggleModifier('has_tax_withholding', 'tax')}
                label="Tax withholding"
              />
            )}
          >
            <SimpleTaxWithholdingSection offer={offer} onChangeField={onChangeModifierField} />
          </CollapsibleRow>
          {/* Kept alongside the % marker on the Revenue tab's line items
            * (O-4778). A venue whose tax is a per-ticket ManualTax has no line
            * item to mark — its only tax row is the derived, system-owned
            * manual_tax_forecast_row, which carries no % button and does not
            * appear until this is already on. This switch is the only way those
            * venues can reach the feature. */}
          <CollapsibleRow
            title="Tax after facility fee"
            expanded={expanded.taxAfterFff}
            onToggle={() => toggle('taxAfterFff')}
            control={(
              <ModifierSwitch
                on={!!offer.apply_tax_after_facility_fee}
                onToggle={() => toggleModifier('apply_tax_after_facility_fee', 'taxAfterFff')}
                label="Tax after facility fee"
              />
            )}
          >
            <div className="simple-mod" data-testid="modifiers-subsection-tax-after-facility-fee">
              <p className="text-muted small" style={{ margin: 0 }}>
                Applies this offer’s tax after the facility fee (on the net gate) and adds it on top as pass-through, so it does not reduce the deal.
              </p>
            </div>
          </CollapsibleRow>
          <CollapsibleRow
            title="Merchandising"
            expanded={expanded.merch}
            onToggle={() => toggle('merch')}
            control={(
              <ModifierSwitch
                on={!!offer.has_merchandising}
                onToggle={() => toggleModifier('has_merchandising', 'merch')}
                label="Merchandising"
              />
            )}
          >
            <SimpleMerchandisingSection offer={offer} onChangeField={onChangeModifierField} />
          </CollapsibleRow>
          <CollapsibleRow
            title="Promoter Profit"
            expanded={expanded.promoter}
            onToggle={() => toggle('promoter')}
            control={(
              <ModifierSwitch
                on={!!offer.promoter_profit}
                onToggle={() => toggleModifier('promoter_profit', 'promoter')}
                label="Promoter profit"
              />
            )}
          >
            <PromoterProfitSection offer={offer} onChangeField={onChangeModifierField} />
          </CollapsibleRow>
        </section>
      )}

      {!isCreate && ctrl.team?.package?.multi_artist_v3_offers && ctrl.poolActive && (
        <SimplePoolShareSection
          offer={offer}
          earnings={ctrl.calculated?.artistEarnings || null}
          onChangeField={onChangeModifierField}
        />
      )}

      {!isCreate && (
        <div className="simple-deal-form__payout" data-testid="headliner-payout">
          <span className="simple-deal-form__payout-label">Total Headliner Payout</span>
          <span className="simple-deal-form__payout-value">
            {ctrl.formatCalculatedNumber(ctrl.artistEarningsOffer || 0)}
          </span>
        </div>
      )}

      <DealFormActions saving={saving} dirty={dirty} onCancel={onClose} onSave={handleSave} />
    </div>
  );
};

SimpleCreateArtistDealForm.propTypes = {
  ctrl: PropTypes.shape({
    offer: PropTypes.object.isRequired,
    budget: PropTypes.object,
    team: PropTypes.object.isRequired,
    csrfToken: PropTypes.string.isRequired,
    updateOffer: PropTypes.func.isRequired,
    handleSelectOffer: PropTypes.func,
    formatCalculatedNumber: PropTypes.func.isRequired,
    artistEarningsOffer: PropTypes.number,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(['create', 'edit']),
  defaultRole: PropTypes.string,
};

SimpleCreateArtistDealForm.defaultProps = {
  mode: 'edit',
  defaultRole: 'headline',
};

export default SimpleCreateArtistDealForm;
